import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Clock, Award, User } from "lucide-react";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";

export default function ProgramDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/programs/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);

  if (p === false) return <Section className="pt-40 pb-40"><Container><DisplayH1>Program not found.</DisplayH1></Container></Section>;
  if (!p) return <Section className="pt-40 pb-40"><Container><div className="text-[color:var(--wk-bone-dim)]">Loading…</div></Container></Section>;

  return (
    <>
      <Section testid="program-hero" className="relative h-[90svh] min-h-[640px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={p.image} alt="" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 cinema-overlay" />
        </div>
        <Container className="relative z-10 h-full flex flex-col justify-end pb-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <Eyebrow>{p.level} · {p.duration}</Eyebrow>
            <DisplayH1 className="mt-6 max-w-4xl">{p.name}</DisplayH1>
            <p className="mt-6 text-xl text-[color:var(--wk-bone-dim)] max-w-2xl leading-relaxed">{p.tagline}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/apply" data-testid="program-apply" className="btn-gold">Apply to {p.name} <ArrowRight size={16} /></Link>
              <Link to="/assistant" className="btn-ghost">Ask TONIA about this program</Link>
            </div>
          </motion.div>
        </Container>
      </Section>

      <Section testid="program-meta" className="py-16 border-b border-white/10">
        <Container>
          <div className="grid gap-8 md:grid-cols-4">
            {[
              { Icon: Clock, label: "Duration", value: p.duration },
              { Icon: Award, label: "Level", value: p.level },
              { Icon: User, label: "Instructor", value: p.instructor },
              { Icon: Check, label: "Investment", value: p.investment },
            ].map(({ Icon, label, value }) => (
              <div key={label} className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-[color:var(--wk-gold)]"><Icon size={16} /><div className="overline text-[10px]">{label}</div></div>
                <div className="font-serif-display text-2xl">{value}</div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section testid="program-body" className="py-28">
        <Container>
          <div className="grid lg:grid-cols-12 gap-16">
            <Reveal className="lg:col-span-7">
              <Eyebrow>Curriculum</Eyebrow>
              <div className="mt-6 space-y-4">
                {p.curriculum.map((c, i) => (
                  <div key={c} className="flex gap-6 items-start pb-4 border-b border-white/10">
                    <div className="font-serif-display text-3xl text-[color:var(--wk-gold)] w-12">0{i + 1}</div>
                    <div className="font-serif-display text-2xl leading-tight">{c}</div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="card-lux p-8 sticky top-28">
                <Eyebrow>Outcomes</Eyebrow>
                <ul className="mt-4 space-y-3">
                  {p.outcomes.map((o) => (
                    <li key={o} className="flex gap-3 items-start text-[color:var(--wk-bone)]">
                      <Check size={16} className="text-[color:var(--wk-gold)] mt-1 shrink-0" />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="overline">Prerequisites</div>
                  <p className="mt-2 text-sm text-[color:var(--wk-bone-dim)] leading-relaxed">{p.prerequisites}</p>
                </div>
                <div className="mt-8">
                  <Link to={`/apply?program=${p.slug}`} className="btn-gold w-full">Apply Now</Link>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </>
  );
}
